import React, { useCallback, useEffect, useState } from 'react';
import { useAbility } from '@casl/react';
import { Archive, ArchiveRestore, Edit, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { CategoryDialog } from '../../components/learning/CategoryDialog';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { CourseCategory } from '../../types/learning.types';
import { learningApi } from '../../utils/learning-api';
import { courseCategoryStatePillClass } from '../../utils/learning';

const StatePill: React.FC<{ archived: boolean }> = ({ archived }) => (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${courseCategoryStatePillClass(archived)}`}>
        {archived ? 'Archived' : 'Active'}
    </span>
);

export default function CourseCategoriesPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_CATEGORY);

    const [categories, setCategories] = useState<CourseCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<CourseCategory | null>(null);

    const load = useCallback(async () => {
        try {
            setCategories(await learningApi.categories.list());
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canRead) load();
    }, [canRead, load]);

    const handleToggle = async (category: CourseCategory) => {
        try {
            if (category.archived) {
                await learningApi.categories.restore(category.id);
                toast.success(`${category.name} restored`);
            } else {
                await learningApi.categories.archive(category.id);
                toast.success(`${category.name} archived`);
            }
            load();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to update category');
        }
    };

    const openNew = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEdit = (category: CourseCategory) => {
        setEditing(category);
        setDialogOpen(true);
    };

    if (!canRead) return <AccessRestricted />;

    const rowActions = (category: CourseCategory) => (
        <div className="flex justify-end gap-2">
            <Can I={ACTIONS.UPDATE} a={RESOURCES.COURSE_CATEGORY}>
                <Button variant="outline" size="sm" onClick={() => openEdit(category)}>
                    <Edit size={16} />
                    Edit
                </Button>
            </Can>
            <Can I={ACTIONS.UPDATE} a={RESOURCES.COURSE_CATEGORY}>
                <Button variant="outline" size="sm" onClick={() => handleToggle(category)}>
                    {category.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                    {category.archived ? 'Restore' : 'Archive'}
                </Button>
            </Can>
        </div>
    );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <PageHeader
                title="Course Categories"
                description="Archiving a category leaves courses already using it untouched — it just stops appearing in the picker."
                actions={
                    <Can I={ACTIONS.CREATE} a={RESOURCES.COURSE_CATEGORY}>
                        <Button onClick={openNew}>
                            <Plus size={16} />
                            New Category
                        </Button>
                    </Can>
                }
            />

            {loading ? (
                <LoadingPageSkeleton />
            ) : categories.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No categories yet.</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {categories.map((category) => (
                            <Card key={category.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{category.name}</p>
                                        <div className="mt-1 flex items-center gap-2">
                                            <span
                                                className="h-4 w-4 rounded"
                                                style={{ backgroundColor: category.colour }}
                                            />
                                            <span className="font-mono text-xs text-muted-foreground">
                                                {category.colour}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-muted-foreground">
                                            {category.courseCount} courses
                                        </p>
                                    </div>
                                    <StatePill archived={category.archived} />
                                </div>
                                <div className="mt-3">{rowActions(category)}</div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Category</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Colour</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Courses</TableHead>
                                        <TableHead className="font-semibold text-slate-900">State</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {categories.map((category) => (
                                        <TableRow key={category.id}>
                                            <TableCell className="font-medium">{category.name}</TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="h-4 w-4 rounded"
                                                        style={{ backgroundColor: category.colour }}
                                                    />
                                                    <span className="font-mono text-xs text-muted-foreground">
                                                        {category.colour}
                                                    </span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {category.courseCount} courses
                                            </TableCell>
                                            <TableCell>
                                                <StatePill archived={category.archived} />
                                            </TableCell>
                                            <TableCell>{rowActions(category)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <CategoryDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                category={editing}
                onSaved={() => load()}
            />
        </div>
    );
}
